/**
 * HUB-3a: shared collection-grouped style cards for Styles dropdown + side sheet.
 */

import { LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HubCollection, HubStyleCard } from "@/lib/fetchStyles";
import { SignedOutputImage } from "@/features/footwear-studio/SignedOutputImage";

export type HubStageFilter = "all" | "master" | "angles" | "orbit";
export type HubSortKey = "name" | "style_number" | "has_orbit";

export type HubStylesFilterState = {
  search: string;
  collectionFilter: string; // "all" | collection id
  typeFilter: string; // "all" | shoe_type value
  stageFilter: HubStageFilter;
  sort: HubSortKey;
};

export const DEFAULT_HUB_STYLES_FILTERS: HubStylesFilterState = {
  search: "",
  collectionFilter: "all",
  typeFilter: "all",
  stageFilter: "all",
  sort: "style_number",
};

function styleMatchesSearch(style: HubStyleCard, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const name = (style.name ?? "").toLowerCase();
  const num =
    style.style_number != null ? String(style.style_number).toLowerCase() : "";
  return name.includes(q) || num.includes(q);
}

export function styleMatchesStage(style: HubStyleCard, stage: HubStageFilter): boolean {
  if (stage === "all" || stage === "master") return true;
  if (stage === "angles") return style.has_angles;
  if (stage === "orbit") return style.has_orbit;
  return true;
}

function styleMatchesType(style: HubStyleCard, typeFilter: string): boolean {
  if (typeFilter === "all") return true;
  return (style.shoe_type ?? "").trim() === typeFilter;
}

function compareStyles(a: HubStyleCard, b: HubStyleCard, sort: HubSortKey): number {
  if (sort === "has_orbit") {
    if (a.has_orbit !== b.has_orbit) return a.has_orbit ? -1 : 1;
    return compareStyles(a, b, "style_number");
  }
  if (sort === "name") {
    return (a.name ?? "").localeCompare(b.name ?? "", undefined, {
      sensitivity: "base",
    });
  }
  // style_number (default)
  const an = a.style_number;
  const bn = b.style_number;
  if (an == null && bn != null) return 1;
  if (an != null && bn == null) return -1;
  if (an != null && bn != null && an !== bn) return an - bn;
  return (a.name ?? "").localeCompare(b.name ?? "", undefined, {
    sensitivity: "base",
  });
}

/** Client-side filter/sort over hubStyles. Omits empty collections. */
export function filterHubCollections(
  collections: HubCollection[] | null | undefined,
  filters: HubStylesFilterState
): HubCollection[] {
  const { search, collectionFilter, typeFilter, stageFilter, sort } = filters;

  return (collections ?? [])
    .filter(
      (c) => collectionFilter === "all" || c.id === collectionFilter
    )
    .map((c) => {
      const styles = (c.styles ?? [])
        .filter(
          (s) =>
            styleMatchesSearch(s, search) &&
            styleMatchesType(s, typeFilter) &&
            styleMatchesStage(s, stageFilter)
        )
        .slice()
        .sort((a, b) => compareStyles(a, b, sort));
      return { ...c, styles };
    })
    .filter((c) => c.styles.length > 0);
}

export function countHubStyles(
  collections: HubCollection[] | null | undefined
): {
  styles: number;
  collections: number;
} {
  const list = collections ?? [];
  let styles = 0;
  for (const c of list) styles += (c.styles ?? []).length;
  return { styles, collections: list.length };
}

function collectionHeaderLabel(collection: HubCollection): string {
  const metaParts = [
    collection.season,
    collection.quarter,
    collection.year != null ? String(collection.year) : null,
  ].filter((p): p is string => typeof p === "string" && p.trim().length > 0);
  const headerLabel = [collection.name, ...metaParts]
    .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    .join(" · ");
  return headerLabel || "Untitled collection";
}

function StyleCard({
  style,
  isActive,
  onSelect,
}: {
  style: HubStyleCard;
  isActive: boolean;
  onSelect?: (styleId: string) => void;
}) {
  const styleLabel =
    style.name?.trim() ||
    (style.style_number != null
      ? `Style ${style.style_number}`
      : "Untitled style");

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={
        onSelect
          ? () => {
              onSelect(style.id);
            }
          : undefined
      }
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(style.id);
              }
            }
          : undefined
      }
      className={cn(
        "flex gap-2.5 px-3 py-2.5 w-full text-left transition-colors",
        onSelect
          ? isActive
            ? "bg-sky-500/15 hover:bg-sky-500/20 cursor-pointer"
            : "hover:bg-white/3 cursor-pointer"
          : "hover:bg-white/3"
      )}
    >
      <div className="w-12 h-12 rounded-md overflow-hidden bg-bgPage border border-white/8 shrink-0">
        {style.thumb_url ? (
          <SignedOutputImage
            storageRef={style.thumb_url}
            alt={styleLabel}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-textFaint">
            <LayoutGrid className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[11px] font-medium text-textPrimary truncate">
            {styleLabel}
          </span>
          {isActive && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shrink-0">
              Active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {style.style_number != null && (
            <span className="text-[9px] font-mono text-textFaint">
              #{style.style_number}
            </span>
          )}
          {style.shoe_type?.trim() && (
            <span className="text-[9px] text-textFaint truncate">
              {style.shoe_type}
            </span>
          )}
          <span
            className={cn(
              "text-[9px] font-medium",
              style.has_angles ? "text-sky-400/80" : "text-textFaint"
            )}
          >
            Multi-View
          </span>
          <span
            className={cn(
              "text-[9px] font-medium",
              style.has_orbit ? "text-sky-400/80" : "text-textFaint"
            )}
          >
            Orbit
          </span>
        </div>
      </div>
    </div>
  );
}

export function HubStylesList({
  collections,
  activeStyleId,
  onSelectStyle,
  emptyMessage = "No styles yet",
  stickyHeaderOffsetClass = "top-[33px]",
}: {
  collections: HubCollection[];
  activeStyleId?: string | null;
  onSelectStyle?: (styleId: string) => void;
  emptyMessage?: string;
  stickyHeaderOffsetClass?: string;
}) {
  if (collections.length === 0) {
    return (
      <div className="px-3 py-4 text-center">
        <p className="text-[10px] text-textMuted">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {collections.map((collection) => (
        <div key={collection.id}>
          <div
            className={cn(
              "px-3 py-1.5 sticky bg-bgSurface/95 border-b border-white/5",
              stickyHeaderOffsetClass
            )}
          >
            <p className="text-[10px] font-medium text-textSecondary truncate">
              {collectionHeaderLabel(collection)}
            </p>
          </div>
          {collection.styles.length === 0 ? (
            <div className="px-3 py-2">
              <p className="text-[10px] text-textFaint">
                No styles in this collection
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {collection.styles.map((style) => (
                <StyleCard
                  key={style.id}
                  style={style}
                  isActive={!!activeStyleId && style.id === activeStyleId}
                  onSelect={onSelectStyle}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
